import React, { useEffect, useState } from "react";
import PasswordInput from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, ShieldWarning, Copy, Check, DeviceMobile, Trash, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

function ChangePasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed — use it on your next login");
      setForm({ current: "", next: "", confirm: "" });
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card flex flex-col gap-3 max-w-md" data-testid="change-password-card">
      <h2 className="text-xl font-bold font-display flex items-center gap-2"><LockKey size={20} weight="duotone" /> Change password</h2>
      <label className="text-xs uppercase tracking-[0.15em] text-[#000000] flex flex-col gap-1">Current password
        <PasswordInput testid="current-password-input" className="input-base" required autoComplete="current-password"
          value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} />
      </label>
      <label className="text-xs uppercase tracking-[0.15em] text-[#000000] flex flex-col gap-1">New password (min 6 characters)
        <PasswordInput testid="new-password-input" className="input-base" required minLength={6} autoComplete="new-password"
          value={form.next} onChange={e => setForm({ ...form, next: e.target.value })} />
      </label>
      <label className="text-xs uppercase tracking-[0.15em] text-[#000000] flex flex-col gap-1">Confirm new password
        <PasswordInput testid="confirm-password-input" className="input-base" required minLength={6} autoComplete="new-password"
          value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} />
      </label>
      <button data-testid="change-password-submit" className="btn-primary" type="submit" disabled={busy || !form.current || !form.next}>
        {busy ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}

export default function MfaSecurityPanel() {
  const { refreshMe } = useAuth();
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // enrollment flow
  const [enroll, setEnroll] = useState(null); // { secret, qr_data_url }
  const [confirmCode, setConfirmCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null); // shown once
  const [copied, setCopied] = useState(false);

  // disable / regenerate
  const [actionCode, setActionCode] = useState("");
  const [mode, setMode] = useState(""); // "" | "disable" | "regen"

  const load = async () => {
    setErr("");
    try {
      const [s, d] = await Promise.all([api.get("/auth/mfa/status"), api.get("/auth/mfa/trusted-devices")]);
      setStatus(s.data); setDevices(d.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/enroll/start"); setEnroll(data); setConfirmCode(""); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/confirm", { code: confirmCode.trim() });
      setBackupCodes(data.backup_codes); setEnroll(null);
      await load(); await refreshMe();
      toast.success("Two-factor authentication enabled");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runAction = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (mode === "disable") {
        await api.post("/auth/mfa/disable", { code: actionCode.trim() });
        toast.success("Two-factor authentication disabled");
        setBackupCodes(null);
      } else if (mode === "regen") {
        const { data } = await api.post("/auth/mfa/backup-codes/regenerate", { code: actionCode.trim() });
        setBackupCodes(data.backup_codes);
        toast.success("New backup codes generated");
      }
      setMode(""); setActionCode("");
      await load(); await refreshMe();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revoke = async (device_id) => {
    try { await api.post("/auth/mfa/trusted-devices/revoke", { device_id }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const revokeAll = async () => {
    try { await api.post("/auth/mfa/trusted-devices/revoke-all"); await load(); toast.success("All trusted devices revoked"); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((backupCodes || []).join("\n"));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const enabled = status?.mfa_enabled;

  return (
    <div className="flex flex-col gap-5">
    <ChangePasswordCard />
    <div className="grid lg:grid-cols-2 gap-5" data-testid="mfa-security-panel">
      {/* MFA status / enrollment */}
      <div className="card flex flex-col gap-4">
        <div className="flex items-center gap-2">
          {enabled ? <ShieldCheck size={24} weight="duotone" color="#84CC16" /> : <ShieldWarning size={24} weight="duotone" color="#FF5100" />}
          <h2 className="text-xl font-bold font-display">Two-factor authentication</h2>
        </div>
        {status && (
          <div data-testid="mfa-status" className="text-sm">
            Status: <span className={"font-bold " + (enabled ? "text-[#3F6212]" : "text-[#FF5100]")}>{enabled ? "Enabled" : "Not enabled"}</span>
            {enabled && <span className="text-[#000000]"> · {status.backup_codes_remaining}/{status.backup_codes_total} backup codes left</span>}
          </div>
        )}
        {err && <div className="text-[#FF5100] text-sm">{err}</div>}

        {/* Backup codes display (shown once) */}
        {backupCodes && (
          <div data-testid="mfa-backup-codes" className="rounded-xl border border-[#BEF264] bg-[#F7FEE7] p-4">
            <div className="font-bold text-[#3F6212] mb-1">Save your backup codes</div>
            <p className="text-xs text-[#000000] mb-2">Each code works once. Store them somewhere safe — they won't be shown again.</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-[#000000]">
              {backupCodes.map((c) => <span key={c}>{c}</span>)}
            </div>
            <button className="btn-secondary text-xs mt-3 flex items-center gap-1" style={{padding:"3px 12px"}} onClick={copyCodes}>
              {copied ? <Check size={13} weight="bold" /> : <Copy size={13} />} {copied ? "Copied" : "Copy all"}
            </button>
          </div>
        )}

        {/* Enrollment QR step */}
        {enroll ? (
          <form onSubmit={confirmEnroll} className="flex flex-col gap-3">
            <p className="text-sm text-[#000000]">Scan this with Google Authenticator (or any TOTP app), then enter the 6-digit code to confirm.</p>
            <img src={enroll.qr_data_url} alt="MFA QR code" data-testid="mfa-qr" className="w-44 h-44 rounded-lg border border-[#C4C8CE] bg-white p-2" />
            <div className="text-xs text-[#000000]">Can't scan? Enter this key manually: <span className="font-mono text-[#000000] break-all">{enroll.secret}</span></div>
            <input data-testid="mfa-confirm-code" className="input-base font-mono tracking-[0.3em] text-center" value={confirmCode} onChange={(e)=>setConfirmCode(e.target.value)} placeholder="123456" inputMode="numeric" required />
            <div className="flex gap-2">
              <button data-testid="mfa-confirm-enroll" className="btn-primary flex-1" disabled={busy}>{busy ? "Verifying…" : "Confirm & enable"}</button>
              <button type="button" className="btn-secondary" onClick={() => setEnroll(null)}>Cancel</button>
            </div>
          </form>
        ) : mode ? (
          <form onSubmit={runAction} className="flex flex-col gap-3">
            <p className="text-sm text-[#000000]">
              {mode === "disable" ? "Enter a current authenticator or backup code to disable MFA." : "Enter a current authenticator code to generate new backup codes (old ones stop working)."}
            </p>
            <input data-testid="mfa-action-code" className="input-base font-mono tracking-[0.2em] text-center" value={actionCode} onChange={(e)=>setActionCode(e.target.value)} placeholder="Code" required />
            <div className="flex gap-2">
              <button data-testid="mfa-action-submit" className="btn-primary flex-1" disabled={busy}>{busy ? "Working…" : (mode === "disable" ? "Disable MFA" : "Generate codes")}</button>
              <button type="button" className="btn-secondary" onClick={() => { setMode(""); setActionCode(""); }}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="flex flex-wrap gap-2">
            {!enabled ? (
              <button data-testid="mfa-enable-button" className="btn-primary" onClick={startEnroll} disabled={busy}>{busy ? "Loading…" : "Enable 2FA"}</button>
            ) : (
              <>
                <button data-testid="mfa-regen-button" className="btn-secondary" onClick={() => setMode("regen")}>Regenerate backup codes</button>
                <button data-testid="mfa-disable-button" className="btn-secondary" style={{background:"#FEE2E2",color:"#B91C1C",borderColor:"#FCA5A5"}} onClick={() => setMode("disable")}>Disable 2FA</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Trusted devices */}
      <div className="card flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DeviceMobile size={22} weight="duotone" color="#0EA5B7" />
            <h2 className="text-xl font-bold font-display">Trusted devices</h2>
          </div>
          {devices.length > 0 && <button data-testid="mfa-revoke-all" className="btn-ghost text-xs text-[#B91C1C] underline" onClick={revokeAll}>Revoke all</button>}
        </div>
        <p className="text-sm text-[#000000]">Devices that skip 2FA for 30 days. Revoke any you don't recognize.</p>
        {devices.length === 0 ? (
          <div className="text-sm text-[#000000]">No trusted devices.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {devices.map((d) => (
              <div key={d.device_id} data-testid={`trusted-device-${d.device_id}`} className="flex items-center justify-between gap-3 rounded-lg border border-[#C4C8CE] p-2.5">
                <div className="min-w-0">
                  <div className="text-sm text-[#000000] truncate">{d.user_agent || "Unknown device"}</div>
                  <div className="text-xs text-[#000000]">Added {new Date(d.created_at).toLocaleDateString()}</div>
                </div>
                <button data-testid={`revoke-device-${d.device_id}`} className="btn-ghost p-1.5 text-[#B91C1C]" title="Revoke" onClick={() => revoke(d.device_id)}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
